import type {
  CreateManagementPayload,
  CreateManagementResponse,
} from '@/domains/academicRisk/academicRiskManagement.entity';
import type { AcademicRiskManagementRepository } from '@/domains/academicRisk/academicRiskManagement.repository';

/**
 * Use Case para crear una nueva gestión de riesgo académico en el sistema.
 *
 * Funcionalidad:
 * - Crea un registro de gestión asociado a una acción académica específica
 * - Permite documentar el seguimiento y manejo de casos de riesgo académico
 * - Persiste la gestión mediante la capa de infraestructura
 *
 * Responsabilidades:
 * - Validar estructura del payload antes de persistir
 * - Coordinar la creación con el repositorio de gestiones
 * - Actuar como intermediario entre dominio e infraestructura
 * - Asegurar integridad de datos de gestión académica
 */
export class CreateAcademicRiskManagementUseCase {
  private repository: AcademicRiskManagementRepository;

  /**
   * Inicializa el UseCase con las dependencias necesarias.
   *
   * @param repository - Repositorio que implementa AcademicRiskManagementRepository para operaciones de gestión
   */
  constructor(repository: AcademicRiskManagementRepository) {
    this.repository = repository;
  }

  /**
   * Ejecuta la creación de una nueva gestión de riesgo académico.
   *
   * @param payload - Datos de la gestión a crear (código ejecución, fecha, detalles, estado, etc.)
   */
  async execute(
    payload: CreateManagementPayload,
  ): Promise<CreateManagementResponse> {
    return await this.repository.createManagement(payload);
  }
}
